import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, HeartHandshake, MapPin, Sparkles, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { formatInCentralTime } from "@/utils/timezone";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface NextWellnessPlanCardProps {
  event: {
    id: string;
    title: string;
    date: string;
    location: string;
    host_logo_url?: string | null;
    category?: string | null;
    distance_mi?: number | null;
    is_cowgirls_hosted?: boolean | null;
  } | null;
  userId: string;
  isRsvped: boolean;
  isMeganGoing?: boolean;
  isLoading?: boolean;
}

const fallbackImages: Record<string, string> = {
  Movement: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=500&fit=crop",
  Mindset: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&h=500&fit=crop",
  Nutrition: "https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=800&h=500&fit=crop",
  "Pop-ups": "https://images.unsplash.com/photo-1533900298318-6b8da08a523e?w=800&h=500&fit=crop",
  Social: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=500&fit=crop",
};

export function NextWellnessPlanCard({ event, userId, isRsvped, isMeganGoing = false, isLoading = false }: NextWellnessPlanCardProps) {
  const navigate = useNavigate();

  const { data: socialProof } = useQuery({
    queryKey: ["home-next-plan-social-proof", event?.id, userId],
    queryFn: async () => {
      if (!event?.id) return { attendeeCount: 0, friendCount: 0, buddyCount: 0 };

      const [{ data: attendances, error: attendanceError }, { data: connections, error: connectionError }] = await Promise.all([
        supabase
          .from("event_attendances")
          .select("user_id, needs_buddy")
          .eq("event_id", event.id)
          .eq("status", "rsvp"),
        supabase
          .from("user_connections")
          .select("connected_user_id")
          .eq("user_id", userId)
          .eq("status", "accepted"),
      ]);

      if (attendanceError) throw attendanceError;
      if (connectionError) throw connectionError;

      const friendIds = new Set((connections ?? []).map((connection) => connection.connected_user_id));
      return {
        attendeeCount: attendances?.length ?? 0,
        friendCount: (attendances ?? []).filter((attendance) => friendIds.has(attendance.user_id)).length,
        buddyCount: (attendances ?? []).filter((attendance) => attendance.needs_buddy).length,
      };
    },
    enabled: !!event?.id && !!userId,
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card className="overflow-hidden rounded-2xl" aria-label="Loading your next wellness plan">
        <Skeleton className="aspect-[16/9] w-full rounded-none" />
        <div className="space-y-3 p-5">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
      </Card>
    );
  }

  if (!event) {
    return (
      <Card className="overflow-hidden rounded-2xl bg-gradient-spa p-5">
        <p className="eyebrow mb-2">Your next wellness plan</p>
        <h2 className="font-display text-2xl font-semibold text-foreground">A fresh plan is just around the bend.</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Explore upcoming gatherings and find the one that feels right for you.
        </p>
        <Button className="mt-5 w-full gap-2" onClick={() => navigate("/events")}>
          Explore Events
          <ArrowRight className="h-4 w-4" />
        </Button>
      </Card>
    );
  }

  const image = event.host_logo_url || fallbackImages[event.category ?? ""] || fallbackImages.Movement;
  const firstSocialSignal = socialProof?.attendeeCount
    ? `${socialProof.attendeeCount} ${socialProof.attendeeCount === 1 ? "person is" : "people are"} going`
    : null;
  const secondSocialSignal = socialProof?.friendCount
    ? `${socialProof.friendCount} ${socialProof.friendCount === 1 ? "friend is" : "friends are"} going too`
    : socialProof?.buddyCount
      ? `${socialProof.buddyCount} open to a buddy`
      : null;

  return (
    <Card className="overflow-hidden rounded-2xl shadow-lift">
      <div className="relative aspect-[16/9] overflow-hidden bg-muted">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-foreground/10 to-transparent" />
        <p className="absolute left-4 top-4 rounded-full bg-card/90 px-3 py-1 text-[11px] font-semibold uppercase text-foreground shadow-soft backdrop-blur-sm">
          Your next wellness plan
        </p>
        {isRsvped && (
          <p className="absolute bottom-4 left-4 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
            You're going
          </p>
        )}
      </div>

      <div className="p-5">
        <h2 className="font-display text-[1.35rem] font-semibold leading-tight text-foreground">{event.title}</h2>
        {event.is_cowgirls_hosted && (
          <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-secondary-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Wellness Cowgirls is hosting
          </p>
        )}
        <div className="mt-3 space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 shrink-0 text-secondary-foreground" />
            <span>{formatInCentralTime(event.date, "EEE, MMM d · h:mm a")}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-secondary-foreground" />
            <span className="line-clamp-1">{event.distance_mi ? `${event.distance_mi} mi away` : event.location}</span>
          </div>
        </div>

        {(firstSocialSignal || secondSocialSignal) && (
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-border/70 pt-3 text-xs font-medium text-foreground">
            {firstSocialSignal && (
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-secondary-foreground" />{firstSocialSignal}</span>
            )}
            {secondSocialSignal && (
              <span className="flex items-center gap-1.5"><HeartHandshake className="h-4 w-4 text-secondary-foreground" />{secondSocialSignal}</span>
            )}
          </div>
        )}
        {isMeganGoing && (
          <p className="mt-3 text-xs font-semibold text-secondary-foreground">Megan is going ✨</p>
        )}

        <Button className="mt-4 w-full gap-2" onClick={() => navigate(`/events/${event.id}`)}>
          {isRsvped ? "View plan" : "View event"}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}